import type { DiscountApprovalStatus, DiscountScope, SalesChannel } from "@kopi/contracts";
import { type DISCOUNT_SORTS, definedOnly, type SortValue } from "@kopi/contracts/client";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";

export const discountsKey = ["discounts"] as const;
const paging = {
  initialPageParam: undefined as string | undefined,
  getNextPageParam: (page: { nextCursor: string | null }) => page.nextCursor ?? undefined,
};

export const discountsQuery = (filters: {
  q: string;
  active?: boolean | undefined;
  scope?: DiscountScope | undefined;
  /** Undefined: the server's default order (newest first). */
  sort?: SortValue<(typeof DISCOUNT_SORTS)[number]> | undefined;
}) =>
  infiniteQueryOptions({
    queryKey: [...discountsKey, "list", filters],
    ...paging,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1.discounts.get({
          query: { limit: 30, ...definedOnly({ ...filters, cursor: pageParam }) },
        }),
      ),
  });

export const discountQuery = (discountId: string) =>
  queryOptions({
    queryKey: [...discountsKey, discountId],
    queryFn: async () => unwrap(await api.v1.discounts({ discountId }).get()),
  });

export const discountPolicyQuery = (outletId: string) =>
  queryOptions({
    queryKey: [...discountsKey, "policy", outletId],
    enabled: Boolean(outletId),
    queryFn: async () => unwrap(await api.v1.outlets({ outletId })["discount-policy"].get()),
  });

export const fetchEligibleDiscounts =
  (outletId: string, scope: DiscountScope, orderType: SalesChannel) =>
  async (q: string, cursor: string | undefined) =>
    unwrap(
      await api.v1.outlets({ outletId }).discounts.eligible.get({
        query: { scope, orderType, limit: 30, ...definedOnly({ q, cursor }) },
      }),
    );

export const discountApprovalsKey = ["discount-approvals"] as const;
export const discountApprovalsQuery = (outletId: string, status?: DiscountApprovalStatus) =>
  infiniteQueryOptions({
    queryKey: [...discountApprovalsKey, "list", outletId, status],
    enabled: Boolean(outletId),
    ...paging,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1.outlets({ outletId })["discount-approvals"].get({
          query: { limit: 30, ...definedOnly({ status, cursor: pageParam }) },
        }),
      ),
  });

export const discountApprovalQuery = (approvalId: string) =>
  queryOptions({
    queryKey: [...discountApprovalsKey, approvalId],
    enabled: Boolean(approvalId),
    queryFn: async () => unwrap(await api.v1["discount-approvals"]({ approvalId }).get()),
    retry: false,
  });
